import React, { useState, useEffect } from 'react';
import { api } from '../services/authService';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { validateDocument, getValidationClass, getValidationMessage } from '../utils/validators';

const CompanyPartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    rg: '',
    position: 'Sócio',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    is_primary: false
  });
  const [cpfTouched, setCpfTouched] = useState(false);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/company-partners');
      setPartners(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar sócios:', error);
      toast.error('Erro ao carregar sócios');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      cpf: '',
      rg: '',
      position: 'Sócio',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      is_primary: false
    });
    setShowForm(true);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name || '',
      cpf: partner.cpf || '',
      rg: partner.rg || '',
      position: partner.position || 'Sócio',
      email: partner.email || '',
      phone: partner.phone || '',
      address: partner.address || '',
      city: partner.city || '',
      state: partner.state || '',
      zip_code: partner.zip_code || '',
      is_primary: partner.is_primary || false
    });
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Tem certeza que deseja excluir o sócio "${name}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/company-partners/${id}`);
      toast.success('Sócio excluído com sucesso!');
      loadPartners();
    } catch (error) {
      console.error('Erro ao excluir sócio:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir sócio');
    }
  };

  const handleSetPrimary = async (id, name) => {
    if (!confirm(`Definir "${name}" como sócio principal?`)) {
      return;
    }

    try {
      await api.post(`/api/company-partners/${id}/set-primary`);
      toast.success('Sócio principal atualizado!');
      loadPartners();
    } catch (error) {
      console.error('Erro ao definir sócio principal:', error);
      toast.error('Erro ao definir sócio principal');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar CPF antes de enviar
    if (formData.cpf && !validateDocument(formData.cpf)) {
      toast.error('CPF inválido! Por favor, corrija antes de salvar.');
      return;
    }

    try {
      if (editingPartner) {
        await api.put(`/api/company-partners/${editingPartner.id}`, formData);
        toast.success('Sócio atualizado com sucesso!');
      } else {
        await api.post('/api/company-partners', formData);
        toast.success('Sócio cadastrado com sucesso!');
      }
      setShowForm(false);
      loadPartners();
    } catch (error) {
      console.error('Erro ao salvar sócio:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar sócio');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando sócios...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <UserIcon className="h-7 w-7 text-blue-600 mr-2" />
            Sócios e Parceiros
          </h2>
          <p className="text-gray-600 mt-1 text-sm">
            Gerencie os sócios da empresa (dados usados nos contratos)
          </p>
        </div>

        <button
          onClick={handleAdd}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Novo Sócio</span>
        </button>
      </div>

      {partners.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhum sócio cadastrado
          </h3>
          <p className="text-gray-500 mb-4">
            Cadastre os sócios da empresa para usar nos contratos
          </p>
          <button
            onClick={handleAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg inline-flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Cadastrar Primeiro Sócio</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className={`bg-white rounded-lg shadow-sm border p-6 ${partner.is_primary ? 'ring-2 ring-yellow-400' : ''}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 rounded-full p-2">
                    <UserIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      {partner.name}
                      {partner.is_primary && (
                        <StarSolidIcon className="h-5 w-5 text-yellow-500" title="Sócio Principal" />
                      )}
                    </h3>
                    <p className="text-sm text-gray-600">{partner.position}</p>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                    title="Editar"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id, partner.name)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                    title="Excluir"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">CPF:</span>
                  <span className="ml-2 text-gray-900 font-mono">{partner.cpf}</span>
                </div>
                {partner.rg && (
                  <div>
                    <span className="text-gray-500">RG:</span>
                    <span className="ml-2 text-gray-900 font-mono">{partner.rg}</span>
                  </div>
                )}
                {partner.email && (
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2 text-gray-900">{partner.email}</span>
                  </div>
                )}
                {partner.phone && (
                  <div>
                    <span className="text-gray-500">Telefone:</span>
                    <span className="ml-2 text-gray-900">{partner.phone}</span>
                  </div>
                )}
                {partner.address && (
                  <div>
                    <span className="text-gray-500">Endereço:</span>
                    <span className="ml-2 text-gray-900">{partner.address}</span>
                  </div>
                )}
                {partner.city && partner.state && (
                  <div>
                    <span className="text-gray-500">Cidade/UF:</span>
                    <span className="ml-2 text-gray-900">{partner.city}/{partner.state}</span>
                  </div>
                )}
              </div>

              {!partner.is_primary && partner.is_active && (
                <div className="mt-4 pt-4 border-t">
                  <button
                    onClick={() => handleSetPrimary(partner.id, partner.name)}
                    className="text-sm text-yellow-600 hover:text-yellow-700 flex items-center gap-1"
                  >
                    <StarIcon className="h-4 w-4" />
                    <span>Definir como principal</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingPartner ? 'Editar Sócio' : 'Novo Sócio'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CPF *
                  </label>
                  <input
                    type="text"
                    name="cpf"
                    value={formData.cpf}
                    onChange={handleChange}
                    onBlur={() => setCpfTouched(true)}
                    required
                    placeholder="000.000.000-00"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${getValidationClass(formData.cpf, cpfTouched) || 'border-gray-300'}`}
                  />
                  {cpfTouched && formData.cpf && (
                    <p className={`text-xs mt-1 ${getValidationMessage(formData.cpf, cpfTouched).isValid ? 'text-green-600' : 'text-red-600'}`}>
                      {getValidationMessage(formData.cpf, cpfTouched).message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RG
                  </label>
                  <input
                    type="text"
                    name="rg"
                    value={formData.rg}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo *
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Sócio Administrador">Sócio Administrador</option>
                    <option value="Sócio">Sócio</option>
                    <option value="Sócio Gerente">Sócio Gerente</option>
                    <option value="Diretor">Diretor</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Estado (UF)
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      maxLength="2"
                      placeholder="MS"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CEP
                    </label>
                    <input
                      type="text"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="00000-000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="is_primary"
                      checked={formData.is_primary}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 flex items-center gap-1">
                      <StarIcon className="h-4 w-4 text-yellow-500" />
                      Marcar como sócio principal (usado nos contratos por padrão)
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPartner ? 'Atualizar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPartnersManager;
